/**
 * Alert — the CRM's one inline message box.
 *
 * The same "colored box with an icon and a line of text" was hand-rolled in
 * dozens of places (load failures, guardrail warnings, quota notices, "no
 * consent on file"), each with its own border/tint/dark-mode guesses. Some
 * drifted off the semantic palette entirely. This is the single recipe, so a
 * red box means the same thing everywhere and dark mode is never forgotten.
 *
 * Two densities, because the inventory found two real ones in the wild: `md`
 * (p-3 / text-sm) is the page-level box; `sm` (p-2.5 / text-xs) is the box that
 * sits inside a card or a drawer. Nothing else about the two differs.
 *
 * `actions` renders inline at the RIGHT of the same row as the body — that is
 * where every existing box-with-a-button already puts its Retry/Undo control,
 * and moving them under the text would be a visible layout change, not a
 * restyle.
 *
 * Semantics, not decoration: an Alert always pairs its color with an icon and
 * words (color alone never carries meaning), and it announces itself — error
 * and warning are `role="alert"` (interrupt), info and success are
 * `role="status"` (polite). Pass `role` only when a specific surface needs to
 * override that.
 */
import { AlertCircle, AlertTriangle, CheckCircle, Info, X } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import type { ReactNode } from 'react';

export type AlertTone = 'info' | 'success' | 'warning' | 'error';

export interface AlertProps {
  tone: AlertTone;
  children: ReactNode;
  /** Bolded first line above the body. */
  title?: string;
  /** Buttons/links, rendered inline at the right edge of the row. */
  actions?: ReactNode;
  /** Renders a dismiss X at the far right. The caller owns the hiding. */
  onDismiss?: () => void;
  /** Leading tone icon (default true). */
  icon?: boolean;
  /** `md` = page-level box (default); `sm` = the denser in-card box. */
  size?: 'md' | 'sm';
  /** Appended last — margins/width only, by convention. */
  className?: string;
  /** Override the tone's default live-region role. */
  role?: 'alert' | 'status';
}

const TONES: Record<AlertTone, { cls: string; Icon: LucideIcon; role: 'alert' | 'status' }> = {
  error: {
    cls: 'border-red-200 bg-red-50 text-red-700 dark:border-red-800 dark:bg-red-900/20 dark:text-red-400',
    Icon: AlertCircle,
    role: 'alert',
  },
  warning: {
    cls: 'border-amber-200 bg-amber-50 text-amber-800 dark:border-amber-800 dark:bg-amber-900/20 dark:text-amber-300',
    Icon: AlertTriangle,
    role: 'alert',
  },
  success: {
    cls: 'border-green-200 bg-green-50 text-green-700 dark:border-green-800 dark:bg-green-900/20 dark:text-green-400',
    Icon: CheckCircle,
    role: 'status',
  },
  info: {
    // text-primary-800, not -700: the pre-kit CRM wrote this box by hand in
    // dozens of places and every single copy used -800 — the kit codifies the
    // real house recipe so the migrated sites render byte-identically.
    cls: 'border-primary-200 bg-primary-50 text-primary-800 dark:border-primary-800 dark:bg-primary-900/20 dark:text-primary-300',
    Icon: Info,
    role: 'status',
  },
};

const SIZES = {
  md: 'p-3 text-sm',
  sm: 'p-2.5 text-xs',
} as const;

export default function Alert({
  tone,
  children,
  title,
  actions,
  onDismiss,
  icon = true,
  size = 'md',
  className = '',
  role,
}: AlertProps) {
  const { cls, Icon, role: defaultRole } = TONES[tone];

  return (
    <div
      role={role ?? defaultRole}
      className={`flex items-start gap-2 rounded-lg border ${SIZES[size]} ${cls} ${className}`}
    >
      {icon && <Icon className="h-4 w-4 flex-shrink-0 mt-0.5" />}
      <div className="min-w-0 flex-1">
        {title && <div className="font-medium">{title}</div>}
        {children}
      </div>
      {actions && <div className="ml-auto flex flex-shrink-0 items-center gap-2">{actions}</div>}
      {onDismiss && (
        <button
          type="button"
          onClick={onDismiss}
          aria-label="Dismiss"
          className="-m-1 flex-shrink-0 rounded p-1 opacity-70 transition-opacity duration-150 hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-primary-500"
        >
          <X className="h-4 w-4" />
        </button>
      )}
    </div>
  );
}
